import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSite, createPendingClients, approveClient, regenerateToken, setSiteActiveStatus, setSiteNotificationStatus, deleteSite, assignSiteCategory, removeSiteCategory } from '../../services/siteManagement/siteManagement.service';
import { getCustomer, updateCustomer, getCustomerSites } from '../../services/customer/customer.service';
import { getAlarmsByCustomer, localAcknowledge } from '../../services/bureauManagement/bureauManagement.service';
import { formatDateTime } from '../../helpers/formatDateTime';
import { getAlarmSections, alarmSectionsToText, alarmSectionsToHtml } from '../../helpers/formatAlarmClipboard';
import { DateTimeDisplay, ActionList, Breadcrumbs, SearchQuery, StatusMessage } from "../../components/ui";
import { TOAST_LONG_MS, TOAST_SUCCESS_MS } from '../../constants/timeouts';
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { AddressMap } from "../../components/maps";
import type { CustomerListItem } from '../../services/customer/customer.types';
import type { BureauAlarm } from '../../services/bureauManagement/bureauManagement.types';
import type { SiteDetail, SiteListItem } from '../../services/siteManagement/siteManagement.types';
import { BureauCategoryTable } from "../../components/alarms/category/views/BureauCategoryTable";
import Pagination from '../../components/ui/Pagination';
import BulkActionToolbar from '../../components/ui/BullActionToolbar';
import BureauAcknowledgeForm from '../../components/forms/BureauAcknowledgeForm';
import BureauFilter from '../../components/filters/BureauFilter';
import { FilterBtn, SlidingSwitchButton } from '../../components/ui/Buttons';
import CustomerForm, { CustomerFormData } from '../../components/forms/CustomerForm';
import { getBureauCategoryLabel } from '../../components/alarms/category/constants';
import Modal from '../../components/ui/Modal';
import { ModalContent } from '../../components/ui/ModalContent';
import { getProfile } from '../../services/userManagement/userManagement.service';
import { listCategories } from '../../services/category/category.service';
import type { CategorySummary } from '../../services/category/category.types';
import { useAuthContext } from '../../contexts/useAuthContext';
import { alarmAcknowledgedEvent } from '../../services/events/alarmEvents';
import { useTreeRefresh } from '../../contexts/TreeRefreshContext';

type CustomerSite = SiteListItem & {
  sirId?: number;
  shId?: number;
  registryStatus?: string;
  modifiedAt?: string;
  name?: string;
  isActive?: boolean;
  siteCategoryId?: number | null;
  siteCategoryName?: string | null;
};

type SiteDetailFallback = SiteDetail & {
  created?: string;
  createdDate?: string;
  updated?: string;
  modifiedAt?: string;
  approvedAt?: string;
  updatedAt?: string;
};

type ClipboardNavigator = Navigator & {
  clipboard?: Navigator['clipboard'] & {
    write?: (items: ClipboardItem[]) => Promise<void>;
  };
};

type ClipboardWindow = Window & typeof globalThis & {
  ClipboardItem?: new (items: Record<string, Blob>) => ClipboardItem;
};

type ClipboardDoc = Document & {
  execCommand?: (commandId: string, showUI?: boolean, value?: string) => boolean;
};

type RequestSubmitForm = HTMLFormElement & {
  requestSubmit?: () => void;
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  const appendCshDetails = (baseMessage: string, cshGroupNames: unknown, cshGroupIds: unknown): string => {
    const names = Array.isArray(cshGroupNames)
      ? cshGroupNames.filter((name): name is string => typeof name === 'string' && name.trim().length > 0)
      : [];
    const ids = Array.isArray(cshGroupIds)
      ? cshGroupIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id) && id > 0)
      : [];

    if (names.length > 0) {
      return `${baseMessage} CSH groups: ${names.join(', ')}`;
    }
    if (ids.length > 0) {
      return `${baseMessage} CSH group IDs: ${ids.join(', ')}`;
    }
    return baseMessage;
  };

  if (error && typeof error === 'object') {
    const errObj = error as {
      message?: unknown;
      error?: unknown;
      cshGroupNames?: unknown;
      cshGroupIds?: unknown;
      response?: {
        data?: {
          message?: unknown;
          error?: unknown;
          cshGroupNames?: unknown;
          cshGroupIds?: unknown;
        };
      };
    };

    const responsePayload = errObj.response?.data;
    const payload = responsePayload && typeof responsePayload === 'object' ? responsePayload : errObj;
    const payloadMessage = payload.error ?? payload.message;

    if (typeof payloadMessage === 'string' && payloadMessage.trim()) {
      return appendCshDetails(payloadMessage.trim(), payload.cshGroupNames, payload.cshGroupIds);
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

const CustomerDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { refreshTree, refreshBureauData } = useTreeRefresh();
  const [customer, setCustomer] = useState<CustomerListItem | null>(null);
  const [sites, setSites] = useState<CustomerSite[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [bureauAlarms, setBureauAlarms] = useState<BureauAlarm[]>([]);
  const [alarmsLoading, setAlarmsLoading] = useState<boolean>(false);
  const [alarmsTotal, setAlarmsTotal] = useState<number>(0);
  const [selectedIds, setSelectedIds] = useState<Record<number, boolean>>({});
  const [alarmsRefreshCount, setAlarmsRefreshCount] = useState<number>(0);
  const [dataRefreshCount, setDataRefreshCount] = useState<number>(0);
  const [bulkMode, setBulkMode] = useState<'acknowledge' | null>(null);
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [ackSuccessMessage, setAckSuccessMessage] = useState<string | null>(null);
  const [ackErrorMessage, setAckErrorMessage] = useState<string | null>(null);
  const { userProfile, userRole, isAuthenticated, isLoading } = useAuthContext();
  const canApproveClients = isAuthenticated && !isLoading && !!userProfile && userRole?.role === 'Admin';
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [pendingClientsModalOpen, setPendingClientsModalOpen] = useState<boolean>(false);
  const [pendingClientData, setPendingClientData] = useState<CustomerSite | null>(null);
  const [selectedSiteForAction, setSelectedSiteForAction] = useState<CustomerSite | null>(null);
  const [regenerateTokenModalOpen, setRegenerateTokenModalOpen] = useState<boolean>(false);
  const [tokenReason, setTokenReason] = useState<string>('');
  const [approvalNotes, setApprovalNotes] = useState<string>('');
  const [approveChange, setApproveChange] = useState<boolean>(true);
  const [generateToken, setGenerateToken] = useState<boolean>(true);
  const [sitesWithNoPendingClients, setSitesWithNoPendingClients] = useState<Set<string>>(new Set());
  const [sitesWithPendingClients, setSitesWithPendingClients] = useState<Set<string>>(new Set());
  const [sitesWithErrors, setSitesWithErrors] = useState<Set<string>>(new Set());
  const [pendingClientStatus, setPendingClientStatus] = useState<Record<string, 'loading' | 'pending' | 'none' | 'error'>>({});
  const [modalError, setModalError] = useState<string | null>(null);
  const [openDeleteSiteModal, setOpenDeleteSiteModal] = useState<boolean>(false);
  const [deleteModalHeadingText, setDeleteModalHeadingText] = useState<string>('');
  const [deleteModalMessageText, setDeleteModalMessageText] = useState<string>('');
  const [deletingSiteIds, setDeletingSiteIds] = useState<Set<string | number>>(new Set());
  const deleteResolverRef = useRef<((value: boolean) => void) | null>(null);
  const [availableCategories, setAvailableCategories] = useState<CategorySummary[]>([]);
  const [assignCategoryModalOpen, setAssignCategoryModalOpen] = useState<boolean>(false);
  const [siteForCategoryAssignment, setSiteForCategoryAssignment] = useState<CustomerSite | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [categoryAssignError, setCategoryAssignError] = useState<string | null>(null);
  const [assigningCategory, setAssigningCategory] = useState<boolean>(false);
  // Search and filter state for bureau alarms
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('active-not-ack');
  const [bureauPage, setBureauPage] = useState<number>(1);
  const [bureauPageSize] = useState<number>(100);
  const [bureauTotalPages, setBureauTotalPages] = useState<number>(1);
  const [bureauSortColumn, setBureauSortColumn] = useState<string>('lastOccurrenceAt');
  const [bureauSortDirection, setBureauSortDirection] = useState<'asc' | 'desc'>('desc');
  const [customerCategoryCounts, setCustomerCategoryCounts] = useState<Record<string, number>>({
    all: 0,
    'active-not-ack': 0,
    'active-ack': 0,
    'not-active-not-ack': 0,
  });

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      setError(null);
      setPendingClientStatus({});
      try {
        // Extract customer ID from the key (e.g., "customer-26" -> 26)
        const customerId = Number.parseInt(id.replace('customer-', ''), 10);
        if (Number.isNaN(customerId)) {
          setError('Invalid customer ID');
          return;
        }

        // Get detailed customer information including address
        const customerRes = await getCustomer(customerId);
        if (!customerRes?.success || !customerRes.data) {
          setError('Customer not found');
          return;
        }

        if (!mounted) return;

        // Check if it's an error response
        if ('type' in customerRes.data) {
          setError('Customer not found');
          return;
        }

        setCustomer(customerRes.data as CustomerListItem);
        // Get sites directly from the API with customerId parameter to bypass cache
        let childSites: CustomerSite[] = [];
        try {
          const sitesRes = await getCustomerSites(customerId);
          if (sitesRes?.success && Array.isArray(sitesRes.data)) {
            childSites = sitesRes.data.map((site) => ({
              id: site.id,
              name: site.siteName ?? site.displayName,
              siteName: site.siteName ?? site.displayName ?? '',
              displayName: site.displayName ?? site.siteName,
              siteIdentifier: site.siteIdentifier ?? '',
              siteGuid: site.siteGuid,
              sirId: site.id,
              shId: site.siteHierarchyId,
              status: site.status,
              registryStatus: site.status,
              createdAt: site.createdAt,
              updatedAt: site.updatedAt,
              modifiedAt: site.updatedAt,
              approvedAt: site.approvedAt,
              isActive: site.isActive,
              isNotificationEnabled: site.isNotificationEnabled,
              siteCategoryId: site.siteCategoryId,
              siteCategoryName: site.siteCategoryName,
            }));
          }
        } catch (e) {
          // Sites loading failed, but don't fail the whole page
           console.info('Failed to load customer sites:', e);
        }

        if (!mounted) return;
        setSites(childSites);

        // Check for pending clients for all sites after sites load
        if (canApproveClients && childSites.length > 0) {
          const statusUpdates: Record<string, 'loading' | 'pending' | 'none' | 'error'> = {};
          childSites.forEach(site => {
            if (site.siteGuid) {
              statusUpdates[site.siteGuid] = 'loading';
            }
          });
          setPendingClientStatus(statusUpdates);

          // Check pending clients for each site
          const pendingChecks = childSites.map(async (site) => {
            const siteGuid = site.siteGuid;
            if (!siteGuid) {
              return;
            }
            try {
              const result = await createPendingClients({ filterBySiteGuid: siteGuid });
              if (!mounted) return;
              if (!result.success) throw new Error(result.error?.message || 'Failed to fetch pending clients');
              setPendingClientStatus(prev => ({
                ...prev,
                [siteGuid]: result?.success && result.data?.pendingClients && result.data.pendingClients.length > 0 ? 'pending' : 'none'
              }));
            } catch (err) {
              if (!mounted) return;
              console.info('Error fetching pending clients:', err);
              setPendingClientStatus(prev => ({
                ...prev,
                [siteGuid]: 'error'
              }));
            }
          });

          await Promise.allSettled(pendingChecks);
        }

      } catch (e: unknown) {
        // Only update state if component is still mounted
        if (mounted) {
          setError(getErrorMessage(e, 'Failed to load customer details'));
        }
      } finally {
        // Only update state if component is still mounted
        if (mounted) {
          setLoading(false);
        }
      }
    };
    void fetch();
    return () => { mounted = false; };
  }, [id, dataRefreshCount, canApproveClients]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await listCategories();
        if (res?.success && res.data) {
          setAvailableCategories(res.data);
        }
      } catch (err) {
        console.info('Failed to load categories', err);
      }
    };

    void fetchCategories();
  }, []);

  useEffect(() => {
    if (!id) {
      return;
    }
    let mounted = true;
    const fetchAlarms = async () => {
      const customerId = Number.parseInt(id.replace('customer-', ''), 10);
      if (Number.isNaN(customerId)) {
        return;
      }

      setAlarmsLoading(true);
      try {
        const alarmsRes = await getAlarmsByCustomer(customerId, {
          category: selectedCategory !== 'all' ? selectedCategory : undefined,
          page: bureauPage,
          pageSize: bureauPageSize,
          sortBy: bureauSortColumn,
          sortDirection: bureauSortDirection,
        });

        const pageAlarms = alarmsRes?.data?.alarms || alarmsRes?.data?.bureauAlarms || [];
        const sortedPageAlarms = sortBureauAlarmsForFallback(pageAlarms);
        if (!mounted) {
          return;
        }
        setBureauAlarms(sortedPageAlarms);
        setAlarmsTotal(alarmsRes?.data?.totalRecords ?? pageAlarms.length);
        setBureauTotalPages(alarmsRes?.data?.totalPages ?? 1);
        setSelectedIds(prev => {
          const keys = Object.keys(prev).map(k => Number(k));
          const valid = new Set(sortedPageAlarms.map(a => Number(a.bureauAlarmId || a.id || 0)).filter(Boolean));
          const next: Record<number, boolean> = {};
          for (const k of keys) {
            if (valid.has(k)) next[k] = true;
          }
          return next;
        });
      } finally {
        if (mounted) {
          setAlarmsLoading(false);
        }
      }
    };

    void fetchAlarms();
    return () => {
      mounted = false;
    };
  }, [id, alarmsRefreshCount, selectedCategory, bureauPage, bureauPageSize, bureauSortColumn, bureauSortDirection]);

  useEffect(() => {
    return alarmAcknowledgedEvent.subscribe((payload) => {
      if (payload?.source === 'signalr' && payload?.eventType === 'BureauAlarmUpdated') {
        return;
      }
      setAlarmsRefreshCount(prev => prev + 1);
    });
  }, []);

  useEffect(() => {
    const handleRuleLifecycleEvent = () => {
      setDataRefreshCount(prev => prev + 1);
      setAlarmsRefreshCount(prev => prev + 1);
    };

    const eventNames = ['rules:created', 'rules:updated', 'rules:deleted', 'rules:status-changed'];
    eventNames.forEach(eventName => {
      globalThis.addEventListener(eventName, handleRuleLifecycleEvent as EventListener);
    });

    return () => {
      eventNames.forEach(eventName => {
        globalThis.removeEventListener(eventName, handleRuleLifecycleEvent as EventListener);
      });
    };
  }, []);

  // Fetch stable per-category totals for customer filter badges.
  useEffect(() => {
    let mounted = true;

    const fetchCustomerCategoryCounts = async () => {
      if (!id) {
        return;
      }

      const customerId = Number.parseInt(id.replace('customer-', ''), 10);
      if (Number.isNaN(customerId)) {
        return;
      }

      try {
        const [activeNotAckRes, activeAckRes, notActiveNotAckRes] = await Promise.all([
          getAlarmsByCustomer(customerId, { category: 'active-not-ack', page: 1, pageSize: 1 }),
          getAlarmsByCustomer(customerId, { category: 'active-ack', page: 1, pageSize: 1 }),
          getAlarmsByCustomer(customerId, { category: 'not-active-not-ack', page: 1, pageSize: 1 }),
        ]);

        if (!mounted) {
          return;
        }

        const activeNotAckTotal = activeNotAckRes?.data?.totalRecords ?? 0;
        const activeAckTotal = activeAckRes?.data?.totalRecords ?? 0;
        const notActiveNotAckTotal = notActiveNotAckRes?.data?.totalRecords ?? 0;

        setCustomerCategoryCounts({
          all: activeNotAckTotal + activeAckTotal + notActiveNotAckTotal,
          'active-not-ack': activeNotAckTotal,
          'active-ack': activeAckTotal,
          'not-active-not-ack': notActiveNotAckTotal,
        });
      } catch (countsErr) {
        console.info('[CustomerDetails] Error loading customer category counts:', countsErr);
      }
    };

    void fetchCustomerCategoryCounts();

    return () => {
      mounted = false;
    };
  }, [id, alarmsRefreshCount]);

  const anySelected = Object.values(selectedIds || {}).some(v => v === true);

  const handleClearFilter = () => {
    setSelectedCategory('active-not-ack');
    setSearchQuery('');
  };

  const [showSiteToggleConfirm, setShowSiteToggleConfirm] = useState(false);
  const [siteToToggle, setSiteToToggle] = useState<CustomerSite | null>(null);
  const [siteToggleTargetActive, setSiteToggleTargetActive] = useState<boolean>(true);

  const isFilterActive = selectedCategory !== 'active-not-ack';

  const mapBureauSortColumnToServer = (column: string): string => {
    switch (column) {
      case 'last': return 'lastOccurrenceAt';
      case 'path': return 'pathToAlarm';
      case 'sourceAlarmState': return 'bureauAlarmState';
      case 'ack': return 'localAck';
      default: return column;
    }
  };

  const sortBureauAlarmsForFallback = (alarms: BureauAlarm[]): BureauAlarm[] => {
    if (!Array.isArray(alarms) || alarms.length <= 1) {
      return alarms;
    }

    const key = bureauSortColumn;
    if (!['siteName', 'path', 'pathToAlarm', 'last', 'lastOccurrenceAt'].includes(key)) {
      return alarms;
    }

    const copy = [...alarms];
    copy.sort((a, b) => {
      let result = 0;

      if (key === 'siteName') {
        const av = String(a.siteName || a.siteIdentifier || '').toLowerCase();
        const bv = String(b.siteName || b.siteIdentifier || '').toLowerCase();
        result = av.localeCompare(bv);
      } else if (key === 'path' || key === 'pathToAlarm') {
        const av = String(a.pathToAlarm || a.path || a.alarmIdentifier || '').toLowerCase();
        const bv = String(b.pathToAlarm || b.path || b.alarmIdentifier || '').toLowerCase();
        result = av.localeCompare(bv);
      } else if (key === 'last' || key === 'lastOccurrenceAt') {
        const av = a.lastOccurrenceAt ? new Date(a.lastOccurrenceAt).getTime() : 0;
        const bv = b.lastOccurrenceAt ? new Date(b.lastOccurrenceAt).getTime() : 0;
        result = av - bv;
      }

      if (bureauSortDirection === 'asc') {
        return result;
      }
      return -result;
    });

    return copy;
  };

  const handleBureauSort = (column: string) => {
    const mappedColumn = mapBureauSortColumnToServer(column);
    let nextDirection: 'asc' | 'desc' = 'asc';
    if (bureauSortColumn === mappedColumn) {
      if (bureauSortDirection === 'asc') {
        nextDirection = 'desc';
      } else {
        nextDirection = 'asc';
      }
    }
    setBureauSortColumn(mappedColumn);
    setBureauSortDirection(nextDirection);
    setBureauPage(1);
  };

  // Apply search and category filter to bureau alarms
  const filteredBureauAlarms = useMemo(() => {
    let result = [...bureauAlarms];

    // Search query filter
    if (searchQuery && searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((item) => {
        const searchableFields = [
          item.siteName,
          item.siteIdentifier,
          item.pathToAlarm,
          item.path,
          item.alarmIdentifier,
          item.bureauRecipientName,
          item.bureauName,
          item.bureauEndpoint,
          item.bureauAlarmState,
          String(item.priority || ''),
        ];
        
        return searchableFields.some(field => 
          field && String(field).toLowerCase().includes(query)
        );
      });
    }

    return result;
  }, [bureauAlarms, searchQuery]);

  const selectedAlarms = useMemo(() => {
    return filteredBureauAlarms.filter(alarm => !!selectedIds[(alarm.bureauAlarmId || alarm.id) ?? 0]);
  }, [filteredBureauAlarms, selectedIds]);

  const hasUnacknowledgedSelected = useMemo(() => {
    return selectedAlarms.some(a => !a.localAck);
  }, [selectedAlarms]);

  const acknowledgeSelected = () => {
    if (!selectedAlarms || selectedAlarms.length === 0) return;
    setBulkMode('acknowledge');
  };

  const handleUpdateCustomer = async (data: CustomerFormData) => {
    if (!customer?.id) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await updateCustomer(customer.id, {
        customerName: data.customerName,
        address1: data.address1,
        address2: data.address2,
        townCity: data.townCity,
        county: data.county,
        country: data.country,
        postCode: data.postCode,
      });

      if (response?.success && response.data) {
        // Update local customer state
        setCustomer(response.data as CustomerListItem);
        setOpenEditModal(false);
        setSuccessMessage('Customer updated successfully');
        setTimeout(() => setSuccessMessage(null), 4000);
        // Trigger refresh to update any dependent data
        setDataRefreshCount(c => c + 1);
      } else {
        setErrorMessage(response?.error?.message || 'Failed to update customer');
        setTimeout(() => setErrorMessage(null), 4000);
      }
    } catch (err: unknown) {
      setErrorMessage(getErrorMessage(err, 'Failed to update customer'));
      setTimeout(() => setErrorMessage(null), 4000);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle notification enable/disable
  const handleSetSiteNotification = async (site: CustomerSite, enable: boolean) => {
    if (!site.shId) return;
    setActionInFlight(`notif-${site.id}`);
    try {
      const res = await setSiteNotificationStatus(site.shId, enable);
      setSites(prev => prev?.map(s => s.id === site.id ? { ...s, isNotificationEnabled: enable } : s) ?? prev);
      setSuccessMessage(res?.data?.message);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (e) {
      // Log the error
      console.info('Failed to toggle notifications:', e);
      setErrorMessage(`Failed to ${enable ? 'enable' : 'disable'} notifications`);
      setTimeout(() => setErrorMessage(null), 4000);
    } finally {
      setActionInFlight(null);
    }
  };

  const handleBulkCopy = async () => {
    const selected = selectedAlarms;
    if (!selected || selected.length === 0) return;

    try {
      const alarmsData = selected.map((alarm) => {
        const sections = getAlarmSections(alarm);
        return { text: alarmSectionsToText(sections), html: alarmSectionsToHtml(sections) };
      });

      const final = alarmsData.map(d => d.text).join('\n\n---\n\n');
      const finalHtml = alarmsData.map(d => d.html).join('<hr/>');

      // Try Clipboard API with text/html and text/plain; fallback to textarea/contentEditable execCommand copy
      try {
        const clipboardNavigator = navigator as ClipboardNavigator;
        const clipboardWindow = globalThis as ClipboardWindow;

        if (clipboardNavigator.clipboard?.write && clipboardWindow.ClipboardItem) {
          const blobPlain = new Blob([final], { type: 'text/plain' });
          const blobHtml = new Blob([finalHtml], { type: 'text/html' });
          const item = new clipboardWindow.ClipboardItem({ 'text/plain': blobPlain, 'text/html': blobHtml });
          await clipboardNavigator.clipboard.write([item]);
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(final);
        } else {
          const div = document.createElement('div');
          div.style.position = 'fixed';
          div.style.left = '-9999px';
          div.contentEditable = 'true';
          div.innerHTML = finalHtml;
          document.body.appendChild(div);
          const range = document.createRange();
          range.selectNodeContents(div);
          const sel = window.getSelection();
          sel?.removeAllRanges();
          sel?.addRange(range);
          const clipboardDoc = document as ClipboardDoc;
          clipboardDoc.execCommand?.('copy');
          sel?.removeAllRanges();
          document.body.removeChild(div);
        }
      } catch (err) {
        // last resort: plain text
        try {
          await navigator.clipboard.writeText(final);
        } catch (_) {
          const ta = document.createElement('textarea');
          ta.value = final;
          ta.style.position = 'fixed';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          const clipboardDoc = document as ClipboardDoc;
          if (clipboardDoc.execCommand) clipboardDoc.execCommand('copy');
          document.body.removeChild(ta);
        }
      }

      setAckSuccessMessage(`Copied ${selected.length} alarm(s) to clipboard`);
      setTimeout(() => setAckSuccessMessage(null), 4000);
    } catch (err) {
      setAckErrorMessage('Failed to copy selected alarms');
      setTimeout(() => setAckErrorMessage(null), 4000);
    }
  };

  // Handler for viewing pending clients
  const handleViewPendingClients = async (site: CustomerSite) => {
    if (!canApproveClients) {
      setErrorMessage('You do not have permission to approve pending clients.');
      return;
    }
    if (!site.siteGuid) {
      setErrorMessage('Site GUID is missing');
      setTimeout(() => setErrorMessage(null), 4000);
      return;
    }

    setActionInFlight(`pending-${site.id}`);
    setModalError(null);
    try {
      const result = await createPendingClients({ filterBySiteGuid: site.siteGuid });
      if (!result.success) throw new Error(result.error?.message || 'Failed to fetch pending clients');
      const pendingClients = result.data?.pendingClients;
      
      if (result?.success && Array.isArray(pendingClients) && pendingClients.length > 0) {
        // Found pending clients - show modal with the first one (only one at a time per requirement)
        setPendingClientData(pendingClients[0]);
        setSelectedSiteForAction(site);
        setPendingClientsModalOpen(true);
        setApprovalNotes('');
        setApproveChange(true);
        setGenerateToken(true);
        // Update cache states
        setSitesWithPendingClients(prev => new Set(prev).add(site.siteGuid || ''));
        setSitesWithNoPendingClients(prev => {
          const next = new Set(prev);
          next.delete(site.siteGuid || '');
          return next;
        });
        setSitesWithErrors(prev => {
          const next = new Set(prev);
          next.delete(site.siteGuid || '');
          return next;
        });
      } else {
        // No pending clients found
        setSuccessMessage('No pending clients found for this site');
        setTimeout(() => setSuccessMessage(null), 4000);
        // Cache this result
        setSitesWithNoPendingClients(prev => new Set(prev).add(site.siteGuid || ''));
        setSitesWithPendingClients(prev => {
          const next = new Set(prev);
          next.delete(site.siteGuid || '');
          return next;
        });
      }
    } catch (err: unknown) {
      console.info('Error fetching pending clients:', err);
      setErrorMessage(getErrorMessage(err, 'Failed to fetch pending clients'));
      setTimeout(() => setErrorMessage(null), 4000);
      // Cache error state to disable the option
      setSitesWithErrors(prev => new Set(prev).add(site.siteGuid || ''));
      setSitesWithPendingClients(prev => {
        const next = new Set(prev);
        next.delete(site.siteGuid || '');
        return next;
      });
    } finally {
      setActionInFlight(null);
    }
  };

  // Handler for approving a pending client
  const handleApprovePendingClient = async () => {
    if (!canApproveClients) {
      setModalError('You do not have permission to approve pending clients.');
      return;
    }
    if (!selectedSiteForAction?.siteGuid) {
      setModalError('Site GUID is missing');
      return;
    }

    const siteGuid = selectedSiteForAction.siteGuid;
    setActionInFlight('approve-client');
    setModalError(null);
    try {
      const payload = {
        siteGuid: siteGuid,
        approveChange,
        generateToken,
        approvalNotes: approvalNotes.trim() || undefined
      };

      const result = await approveClient(payload);
      
      if (result?.success) {
        setSuccessMessage('Client approved successfully');
        setTimeout(() => setSuccessMessage(null), 4000);
        setPendingClientsModalOpen(false);
        setPendingClientData(null);
        setSelectedSiteForAction(null);
        setApprovalNotes('');
        // Clear cache so we can check again
        setSitesWithNoPendingClients(prev => {
          const next = new Set(prev);
          next.delete(siteGuid);
          return next;
        });
        // Refresh sites data
        setDataRefreshCount(c => c + 1);
      } else {
        setModalError(result?.error?.message || 'Failed to approve client');
      }
    } catch (err: unknown) {
      console.info('Error approving client:', err);
      setModalError(getErrorMessage(err, 'Failed to approve client'));
    } finally {
      setActionInFlight(null);
    }
  };

  // Handler for regenerating token
  const handleRegenerateToken = (site: CustomerSite) => {

    if (!site.siteGuid) {
      setErrorMessage('Site GUID is missing');
      setTimeout(() => setErrorMessage(null), 4000);
      return;
    }

    setSelectedSiteForAction(site);
    setTokenReason('');
    setModalError(null);
    setRegenerateTokenModalOpen(true);
  };

  const handleSetSiteActive = async (site: CustomerSite, makeActive: boolean) => {
    if (!site.shId) return;
    const original = sites ? [...sites] : null;
    setSites(prev => prev?.map(s => s.id === site.id ? { ...s, isActive: makeActive } : s) ?? prev);

    try {
      await setSiteActiveStatus(site.shId, makeActive);
      setSuccessMessage(`Site has been set to ${makeActive ? 'Active' : 'Inactive'}`);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (e) {
      setSites(original);
    }
  };

  const waitForDeleteModalConfirm = (): Promise<boolean> => {
    return new Promise((resolve) => {
      deleteResolverRef.current = resolve;
    });
  };

  const handleOpenAssignCategory = async (site: CustomerSite) => {
    setSiteForCategoryAssignment(site);
    setSelectedCategoryId(site.siteCategoryId != null ? String(site.siteCategoryId) : '');
    setCategoryAssignError(null);
    setAssignCategoryModalOpen(true);

    if (availableCategories.length === 0) {
      try {
        const res = await listCategories();
        if (res?.success && res.data) {
          setAvailableCategories(res.data);
        }
      } catch (err) {
        console.info('Failed to load categories', err);
      }
    }
  };

  const handleAssignCategoryConfirm = async () => {
    if (!siteForCategoryAssignment || !selectedCategoryId) {
      setCategoryAssignError('Please select a category');
      return;
    }

    const category = availableCategories.find(c => String(c.id) === selectedCategoryId);
    if (!category) {
      setCategoryAssignError('Selected category not found');
      return;
    }

    setAssigningCategory(true);
    try {
      const res = await assignSiteCategory(siteForCategoryAssignment.id, category.id);
      if (!res?.success) {
        setCategoryAssignError(res?.error?.message || 'Failed to assign category');
        return;
      }

      setSites(prev => prev?.map(s => {
        if (s.id !== siteForCategoryAssignment.id) return s;
        return { ...s, siteCategoryId: category.id, siteCategoryName: category.name };
      }) ?? prev);
      setAssignCategoryModalOpen(false);
      setSiteForCategoryAssignment(null);
      setSelectedCategoryId('');
      setSuccessMessage(`Category "${category.name}" assigned successfully`);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      setCategoryAssignError(getErrorMessage(err, 'Failed to assign category'));
    } finally {
      setAssigningCategory(false);
    }
  };

  const handleRemoveSiteCategory = async (site: CustomerSite) => {
    setActionInFlight(`category-${site.id}`);
    try {
      const res = await removeSiteCategory(site.id);
      if (!res?.success) {
        setErrorMessage(res?.error?.message || 'Failed to remove category');
        setTimeout(() => setErrorMessage(null), 4000);
        return;
      }

      setSites(prev => prev?.map(s => (s.id === site.id ? { ...s, siteCategoryId: null, siteCategoryName: null } : s)) ?? prev);
      setSuccessMessage('Category removed successfully');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      setErrorMessage(getErrorMessage(err, 'Failed to remove category'));
      setTimeout(() => setErrorMessage(null), 4000);
    } finally {
      setActionInFlight(null);
    }
  };

  const handleRemoveSiteCategoryClick = async (site: CustomerSite) => {
    const displayName = site.siteName || site.displayName || site.siteIdentifier;
    setDeleteModalHeadingText('Remove Category');
    setDeleteModalMessageText(`Are you sure you want to remove the category from "${displayName}"?`);
    setOpenDeleteSiteModal(true);

    const confirmed = await waitForDeleteModalConfirm();
    if (!confirmed) return;

    await handleRemoveSiteCategory(site);
  };

  const handleDeleteSite = async (site: CustomerSite) => {
    const displayName = site.siteName || site.displayName || site.siteIdentifier;
    setDeleteModalHeadingText('Delete Site');
    setDeleteModalMessageText(`Are you sure you want to delete "${displayName}"?`);
    setOpenDeleteSiteModal(true);

    const confirmed = await waitForDeleteModalConfirm();

    if (!confirmed) return;

    const siteHierarchyId = site.shId;
    if (!siteHierarchyId) {
      setErrorMessage(`Failed to delete ${displayName}: Site Hierarchy ID not found`);
      return;
    }

    setDeletingSiteIds(prev => new Set(prev).add(siteHierarchyId));
    try {
      const result = await deleteSite(siteHierarchyId);
      if (result.success) {
        setSuccessMessage(`Site "${displayName}" has been deleted`);
        refreshTree();
        setDataRefreshCount(c => c + 1);
        setTimeout(() => setSuccessMessage(null), 4000);
      } else {
        setErrorMessage(`Failed to delete ${displayName}: ${getErrorMessage(result.error, 'Unknown error')}`);
      }
    } catch (err) {
      setErrorMessage(`Failed to delete ${displayName}: ${getErrorMessage(err, 'Unknown error')}`);
    } finally {
      setDeletingSiteIds(prev => {
        const next = new Set(prev);
        next.delete(siteHierarchyId);
        return next;
      });
    }
  };
      {successMessage && (
        <StatusMessage kind="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </StatusMessage>
      )}

  const handleConfirmRegenerateToken = async () => {
    if (!selectedSiteForAction?.siteGuid) {
      setModalError('Site GUID is missing');
      return;
    }

    const siteGuid = selectedSiteForAction.siteGuid;
    setActionInFlight('regenerate-token');
    setModalError(null);
    try {
      const payload = {
        siteGuid: siteGuid,
        reason: tokenReason.trim() || undefined
      };

      const result = await regenerateToken(payload);
      
      if (result?.success) {
        setSuccessMessage('Token regenerated successfully');
        setTimeout(() => setSuccessMessage(null), 4000);
        setRegenerateTokenModalOpen(false);
        setSelectedSiteForAction(null);
        setTokenReason('');
        // Refresh sites data to show updated token
        setDataRefreshCount(c => c + 1);
      } else {
        setModalError(result?.error?.message || 'Failed to regenerate token');
      }
    } catch (err: unknown) {
      console.info('Error regenerating token:', err);
      setModalError(getErrorMessage(err, 'Failed to regenerate token'));
    } finally {
      setActionInFlight(null);
    }
  };

  const handleBulkAcknowledge = async (alarms: BureauAlarm[]) => {
    const profileResponse = await getProfile();
    if (!profileResponse?.success || !profileResponse.data?.id) {
      throw new Error('Unable to retrieve user profile');
    }

    const acknowledgedBy = profileResponse.data.id;
    const userName = userProfile?.preferred_username || userProfile?.email || userProfile?.name || 'Unknown User';

    const payload = { acknowledgedBy, userName };

    let successCount = 0;
    const errorItems: Array<{ id: number | string; message: string }> = [];

    for (const alarm of alarms) {
      const alarmId = alarm.bureauAlarmId || alarm.id;
      if (!alarmId) {
        continue;
      }
      setActionInFlight(String(alarmId));
      try {
        const response = await localAcknowledge(alarmId, payload);
        if (response?.success) {
          successCount++;
        } else {
          console.info(`Failed to acknowledge alarm ${alarmId}:`, response?.error);
          errorItems.push({ id: alarmId, message: response?.error?.message || 'Unknown error' });
        }
      } catch (err: unknown) {
        console.info(`Error acknowledging alarm ${alarmId}:`, err);
        errorItems.push({ id: alarmId, message: getErrorMessage(err, String(err)) });
      }
    }

    setActionInFlight(null);
    setBulkMode(null);
    setSelectedIds({});

    // Optimistically update the local state for acknowledged alarms
    if (successCount > 0) {
      const acknowledgedIds = new Set(
        alarms
          .filter(a => !errorItems.some(e => e.id === (a.bureauAlarmId || a.id)))
          .map(a => a.bureauAlarmId || a.id)
          .filter(Boolean)
      );
      
      setBureauAlarms(prev => 
        prev.map(alarm => {
          const alarmId = alarm.bureauAlarmId || alarm.id;
          if (alarmId && acknowledgedIds.has(alarmId)) {
            return { ...alarm, localAck: true };
          }
          return alarm;
        })
      );
    }

    // Show status messages
    if (successCount > 0) {
      const successfulAlarms = alarms.filter(a => !errorItems.some(e => e.id === (a.bureauAlarmId || a.id)));
      const siteNames = [...new Set(successfulAlarms.map(a => a.siteName || a.siteIdentifier || String(a.bureauAlarmId || a.id)))].join(', ');
      setAckSuccessMessage(`Acknowledged ${successCount} alarm(s): ${siteNames}`);
      setTimeout(() => setAckSuccessMessage(null), TOAST_SUCCESS_MS);
    }
    if (errorItems.length > 0) {
      const msgs = errorItems.map(e => `ID ${e.id}: ${e.message}`).slice(0, 5).join(' | ');
      setAckErrorMessage(`Failed to acknowledge ${errorItems.length} alarm(s): ${msgs}`);
      setTimeout(() => setAckErrorMessage(null), TOAST_LONG_MS);
    }

    // Refresh from backend to ensure we're in sync
    setTimeout(() => setAlarmsRefreshCount(c => c + 1), 300);

    // Emit event to notify Sidebar to refresh badge counts
    if (successCount > 0) {
      setTimeout(() => {
        alarmAcknowledgedEvent.emit();
      }, 500);
    }
  };


  useEffect(() => {
    if (!sites || sites.length === 0) return;

    const missing = sites.filter(s => !s.updatedAt && !s.modifiedAt && !s.createdAt);
    if (missing.length === 0) return;

    let mounted = true;
    const fetchMissing = async () => {
      try {
        const promises = missing.map(s => {
          const id = s.id ?? s.sirId ?? s.shId ?? s.siteIdentifier;
          return getSite(id).then(res => ({ id, res })).catch(err => ({ id, res: null, err }));
        });

        const results = await Promise.all(promises);
        if (!mounted) return;

        const updatedMap: Record<string, Partial<CustomerSite>> = {};
        results.forEach(r => {
          if (r && r.res && r.res.success && r.res.data) {
            const d = r.res.data as SiteDetailFallback;
            const key = String(r.id);
            updatedMap[key] = {
              createdAt: d.createdAt ?? d.created ?? d.createdDate ?? d.approvedAt ?? updatedMap[key]?.createdAt,
              updatedAt: d.updatedAt ?? d.modifiedAt ?? d.updated ?? d.approvedAt ?? updatedMap[key]?.updatedAt,
              modifiedAt: d.modifiedAt ?? d.updatedAt ?? d.updated ?? d.approvedAt ?? updatedMap[key]?.modifiedAt,
              isActive: d.isActive ?? updatedMap[key]?.isActive,
            };
          }
        });

        if (Object.keys(updatedMap).length > 0) {
          setSites(prev => {
            if (!prev) return prev;
            return prev.map(s => {
              const key = String(s.id ?? s.sirId ?? s.shId ?? s.siteIdentifier);
              if (updatedMap[key]) {
                return { ...s, ...updatedMap[key] };
              }
              return s;
            });
          });
        }
      } catch (e) {
        // ignore errors; best-effort enrichment
        console.info('Failed to fetch missing site details', e);
      }
    };

    void fetchMissing();
    return () => { mounted = false; };
  }, [sites]);

  // Call useBreadcrumbs before any conditional returns (Rules of Hooks)
  const breadcrumbs = useBreadcrumbs({
    customerId: customer?.id,
    customerName: customer?.customerName ?? (customer ? `Customer ${customer.id}` : undefined),
  });

  if (loading || error) {
    return (
      <main>
        <div className="container">
          <section className='page'>
            <h2 className="heading__title heading__title--building">{loading ? 'Loading customer…' : 'Customer details'}</h2>
            <DateTimeDisplay />
          </section>
          <section className="page-section">
            {loading && <LoadingSpinner message="Loading customer…" />}
            {error && <div>Error loading customer: {error}</div>}
          </section>
        </div>
      </main>
    );
  }

  if (!customer) return (
    <main>
      <div className="container">
        <div className="page-section">
          <h2>Customer Not Found</h2>
          <p>The requested customer could not be found.</p>
          <button className="btn btn--secondary" onClick={() => navigate(-1)}>Back</button>
        </div>
      </div>
    </main>
  );

  return (
    <main>
      <div className="container">
        
        <section className='page'>
          <div className="heading__section">
            <div className="heading__title-group">
              <h2 className="heading__title heading__title--segment-building">{customer.customerName ?? `Customer ${customer.id}`}</h2>
              <DateTimeDisplay />
              <Breadcrumbs items={breadcrumbs} />
            </div>
            <div>
              <button type="button" className="btn btn-edit" onClick={() => setOpenEditModal(true)}>Edit Customer</button>
            </div>
          </div>
        </section>

        {(() => {
          const pendingSiteCount = Object.values(pendingClientStatus).filter(s => s === 'pending').length;
          if (!canApproveClients || pendingSiteCount === 0) return null;
          return (
            <section className="pending-clients-banner">
              <div className="pending-clients-banner__inner">
                <span className="pending-clients-banner__icon se-icon se-icon--warning-outline" aria-hidden="true"></span>
                <span className="pending-clients-banner__text">
                  <strong>{pendingSiteCount} site{pendingSiteCount > 1 ? 's have' : ' has'} pending client{pendingSiteCount > 1 ? 's' : ''} awaiting approval.</strong>
                  {' '}Use the <em>Approve Pending Client</em> action in the Sites table below.
                </span>
              </div>
            </section>
          );
        })()}

        {bureauAlarms.length > 0 && (
          <>
            <BulkActionToolbar visible={anySelected && !bulkMode}>
              {hasUnacknowledgedSelected && (
                <button type="button" className="btn btn__toolbar--approve" onClick={acknowledgeSelected} disabled={!anySelected}>
                  Acknowledge Selected
                </button>
              )}
              <button type="button" className="btn btn__toolbar--copy" onClick={handleBulkCopy} disabled={!anySelected}>
                Copy Selected
              </button>
            </BulkActionToolbar>

            {/* Acknowledge-specific status messages */}
            {ackSuccessMessage && (
              <StatusMessage kind="success" onClose={() => setAckSuccessMessage(null)}>
                {ackSuccessMessage}
              </StatusMessage>
            )}
            {ackErrorMessage && (
              <StatusMessage kind="error" onClose={() => setAckErrorMessage(null)}>
                {ackErrorMessage}
              </StatusMessage>
            )}

            {/* Filter toolbar */}
            <section className="alarm-content mb-2">
              <div className="toolbar-content pt-1 pb-1">
                <div className="toolbar-content__left">
                  <h3 className="mb-1">CSH - {getBureauCategoryLabel(selectedCategory)}</h3>
                  <div className="toolbar-content__search">
                    <SearchQuery
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                    />
                  </div>
                </div>

                <div className="toolbar-content__right">
                  <div className="alarm-controls">
                    <div className="filter-container">
                      <FilterBtn 
                        onClick={() => setIsFilterOpen(!isFilterOpen)} 
                        isActive={isFilterActive} 
                        isDropdownOpen={isFilterOpen} 
                      />

                      <BureauFilter
                        isOpen={isFilterOpen}
                        onClose={() => setIsFilterOpen(false)}
                        selectedCategory={selectedCategory}
                        onCategoryChange={(cat) => {
                          setSelectedCategory(cat);
                          setBureauPage(1);
                        }}
                        onClear={handleClearFilter}
                        categoryCounts={customerCategoryCounts}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-2">
              <BureauCategoryTable 
                filteredData={filteredBureauAlarms}
                totalCount={customerCategoryCounts[selectedCategory] ?? alarmsTotal}
                selectedIds={selectedIds}
                onToggleSelect={(id: number) => setSelectedIds(prev => ({ ...prev, [id]: !prev[id] }))}
                onToggleSelectAll={() => {
                  const allSelected = filteredBureauAlarms.every(item => selectedIds[(item.bureauAlarmId || item.id) ?? 0]);
                  if (allSelected) {
                    setSelectedIds({});
                  } else {
                    const newSelected: Record<number, boolean> = {};
                    filteredBureauAlarms.forEach(item => {
                      const id = (item.bureauAlarmId || item.id) ?? 0;
                      if (id) newSelected[id] = true;
                    });
                    setSelectedIds(newSelected);
                  }
                }}
                onAcknowledged={(count?: number, acknowledgedAlarmIds?: number[]) => {
                  // Optimistically update the local state immediately
                  if (acknowledgedAlarmIds && acknowledgedAlarmIds.length > 0) {
                    const ackSet = new Set(acknowledgedAlarmIds);
                    setBureauAlarms(prev => 
                      prev.map(alarm => {
                        const alarmId = alarm.bureauAlarmId || alarm.id;
                        if (alarmId && ackSet.has(alarmId)) {
                          return { ...alarm, localAck: true };
                        }
                        return alarm;
                      })
                    );
                  }
                  
                  if (acknowledgedAlarmIds && acknowledgedAlarmIds.length > 0) {
                    const ackSet = new Set(acknowledgedAlarmIds);
                    const ackedAlarms = filteredBureauAlarms.filter(a => ackSet.has((a.bureauAlarmId || a.id) as number));
                    const siteNames = [...new Set(ackedAlarms.map(a => a.siteName || a.siteIdentifier || String(a.bureauAlarmId || a.id)))].join(', ');
                    setAckSuccessMessage(`Acknowledged ${acknowledgedAlarmIds.length} alarm(s): ${siteNames}`);
                  } else {
                    setAckSuccessMessage(`Acknowledged ${count ?? 1} alarm(s)`);
                  }
                  setTimeout(() => setAckSuccessMessage(null), TOAST_SUCCESS_MS);
                  setTimeout(() => setAlarmsRefreshCount(c => c + 1), 300);
                }}
                sortColumn={bureauSortColumn === 'lastOccurrenceAt' ? 'last' : bureauSortColumn}
                sortOrder={bureauSortDirection}
                onSort={handleBureauSort}
                onResetSort={() => {
                  setBureauSortColumn('lastOccurrenceAt');
                  setBureauSortDirection('desc');
                  setBureauPage(1);
                }}
              />
              {bureauTotalPages > 1 && (
                <Pagination
                  currentPage={bureauPage}
                  totalPages={bureauTotalPages}
                  onPageChange={setBureauPage}
                />
              )}
            </section>

            {/* Bulk Acknowledge Modal */}
            <Modal
              isOpen={bulkMode === 'acknowledge' && selectedAlarms.length > 0}
              title={`Acknowledge ${selectedAlarms.length} Alarm(s)`}
              content={
                <BureauAcknowledgeForm
                  selectedAlarms={selectedAlarms}
                  onAcknowledge={async (alarms) => {
                    await handleBulkAcknowledge(alarms);
                    setBulkMode(null);
                  }}
                  actionInFlight={actionInFlight}
                  onRemoveSelected={(id: number) => setSelectedIds(prev => { const copy = { ...prev }; delete copy[id]; return copy; })}
                  onClose={() => { setBulkMode(null); setSelectedIds({}); }}
                  isInsideModal={true}
                />
              }
              onConfirm={async () => {
                await handleBulkAcknowledge(selectedAlarms);
                setBulkMode(null);
              }}
              onCancel={() => {
                setBulkMode(null);
                setSelectedIds({});
              }}
            />
          </>
        )}

        <section className="page-section mb-2">
          {successMessage && (
            <StatusMessage kind="success" onClose={() => setSuccessMessage(null)}>
              {successMessage}
            </StatusMessage>
          )}
          {errorMessage && (
            <StatusMessage kind="error" onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </StatusMessage>
          )}
          <h3 className='heading__title heading__title--segment-building mt-0 mb-1'>Sites</h3>
          {sites && sites.length > 0 ? (
            <table className="table-responsive">
                <thead>
                  <tr>
                    <th>Site Name</th>
                    <th>Identifier</th>
                    <th>Active / Inactive</th>
                    <th>Status</th>
                    <th>Category</th>
                    <th>Last Updated</th>
                    <th>
                      Notifications
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(sites || []).map((site: CustomerSite) => {
                    const siteGuid = site.siteGuid ?? '';
                    const pendingClientsDisabled = !site.siteGuid || !!actionInFlight || sitesWithNoPendingClients.has(site.siteGuid);
                    const regenerateTokenDisabled = !site.siteGuid || !!actionInFlight;
                    const matchedCategory = availableCategories.find(category => category.id === site.siteCategoryId);
                    const siteCategoryLabel = site.siteCategoryName?.trim() || matchedCategory?.name;
                    const hasSiteCategory = Boolean(siteCategoryLabel);
                
                    return (
                    <tr key={site.id} className={pendingClientStatus[siteGuid] === 'pending' ? 'tr--pending-attention' : undefined}>
                      <td>
                        <div className="site-name-cell">
                          <a
                            className="alarm-name-link site-name-cell__name"
                            href={`/sites/${site.id}`}
                          >
                            {site.siteName ?? site.displayName ?? site.name}
                          </a>
                        </div>
                      </td>
                      <td>
                        <div className="identifier-cell">
                          <span className="identifier-cell__value">{site.siteIdentifier ?? '—'}</span>
                          <br></br>
                          <span className="identifier-cell__registry-id">Registry ID: {site.id}</span>
                        </div>
                      </td>
                      <td>
                        {site.isActive === true && <span className="badge badge--active">Active</span>}
                        {site.isActive === false && <span className="badge badge--inactive">Inactive</span>}
                        {site.isActive === undefined && '—'}
                      </td>
                      <td>{site.registryStatus ?? site.status ?? '—'}</td>
                      <td>
                        {hasSiteCategory ? (
                          <div className="site-category-tag" title={siteCategoryLabel}>
                            <span className="site-category-tag__label">{siteCategoryLabel}</span>
                            <button
                              type="button"
                              className="site-category-tag__close se-icon--close"
                              aria-label="Remove category"
                              disabled={actionInFlight === `category-${site.id}`}
                              onClick={() => { void handleRemoveSiteCategoryClick(site); }}
                            >
                              
                            </button>
                          </div>
                        ) : '—'}
                      </td>
                      <td>{formatDateTime(site.updatedAt ?? site.modifiedAt ?? site.createdAt)}</td>
                      <td>
                        {typeof site.isNotificationEnabled === 'boolean' ? (
                          <SlidingSwitchButton
                            checked={site.isNotificationEnabled}
                            disabled={!!actionInFlight}
                            onChange={() => handleSetSiteNotification(site, !site.isNotificationEnabled)}
                            ariaLabel={site.siteName ? `Toggle notifications for ${site.siteName}` : 'Toggle notifications'}
                          />
                        ) : '—'}
                      </td>
                      <td>
                        <div className="actions">
                          <div className="actions__list">
                              <ActionList
                                customActions={[
                                  {
                                    label: site.isActive ? 'Deactivate' : 'Activate',
                                    onClick: () => {
                                      setSiteToToggle(site);
                                      setSiteToggleTargetActive(!site.isActive);
                                      setShowSiteToggleConfirm(true);
                                    },
                                    className: site.isActive ? 'actions__item actions__item--deactivate' : 'actions__item actions__item--acknowledge',
                                    disabled: !!actionInFlight,
                                  },
                                  {
                                    label: 'View',
                                    onClick: () => {
                                      navigate(`/sites/${site.id}`);
                                    },
                                    className: 'actions__item actions__item--view',
                                  },
                                  ...(canApproveClients ? [{
                                    label: pendingClientStatus[siteGuid] === 'pending' ? 'Approve Pending Client' : 'No Pending Clients',
                                    onClick: () => {
                                      handleViewPendingClients(site);
                                    },
                                    className: 'actions__item actions__item--pending',
                                    disabled: pendingClientsDisabled || sitesWithErrors.has(siteGuid) || pendingClientStatus[siteGuid] === 'none' || pendingClientStatus[siteGuid] === 'error',
                                  }] : []),
                                  {
                                    label: 'Regenerate Token',
                                    onClick: () => {
                                      handleRegenerateToken(site);
                                    },
                                    className: 'actions__item actions__item--token',
                                    disabled: regenerateTokenDisabled,
                                  },
                                  {
                                    label: site.siteCategoryId != null ? 'Update Category' : 'Assign Category',
                                    onClick: () => {
                                      void handleOpenAssignCategory(site);
                                    },
                                    className: 'actions__item actions__item--acknowledge',
                                    disabled: !!actionInFlight,
                                  },
                                  {
                                    label: 'Delete',
                                    onClick: () => {
                                      void handleDeleteSite(site);
                                    },
                                    className: 'actions__item actions__item--delete',
                                    disabled: !!actionInFlight || deletingSiteIds.size > 0,
                                  },
                                ]}
                              />
                          </div>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
          ) : (
            <div>No sites for this customer</div>
          )}
        </section>
        <Modal
          isOpen={showSiteToggleConfirm}
          title={siteToggleTargetActive ? 'Set Site Active' : 'Set Site Inactive'}
          content={
            <div>
              Are you sure you want to set this site as <b>{siteToggleTargetActive ? 'Active' : 'Inactive'}</b>?
            </div>
          }
          onConfirm={async () => {
            if (siteToToggle) {
              await handleSetSiteActive(siteToToggle, siteToggleTargetActive);
            }
            setShowSiteToggleConfirm(false);
            setSiteToToggle(null);
          }}
          onCancel={() => {
            setShowSiteToggleConfirm(false);
            setSiteToToggle(null);
          }}
          okButtonLoading={!!actionInFlight}
        />

        <section className="alarm-content mb-2 p-1">
          <div className ="alarm-content--heading">
            <h3 className="heading heading__title--segment-building mt-0 mb-0">Customer Information</h3>
          </div>

          
          <div className="details-grid">
            <div><strong>Customer ID</strong></div><div>{customer.id}</div>
            <div><strong>Customer Name</strong></div><div>{customer.customerName}</div>
          </div>
        </section>

        <section className="alarm-content mb-2 p-1">
          <div className='row'>
            <div className='col-12-xs col-6-md'>
              <h3 className='heading__title heading__title--info mt-0 mb-1'>Address Details</h3>
              <div className="address-details-list">
                {/* Address Information */}
                <div className="address-detail-item">
                  <span className="address-label">Address Line 1:</span>
                  <span className="address-value">{customer.address1 || '—'}</span>
                </div>
                {customer.address2 && (
                  <div className="address-detail-item">
                    <span className="address-label">Address Line 2:</span>
                    <span className="address-value">{customer.address2}</span>
                  </div>
                )}
                <div className="address-detail-item">
                  <span className="address-label">Town/City:</span>
                  <span className="address-value">{customer.townCity || '—'}</span>
                </div>
                {customer.county && (
                  <div className="address-detail-item">
                    <span className="address-label">County:</span>
                    <span className="address-value">{customer.county}</span>
                  </div>
                )}
                <div className="address-detail-item">
                  <span className="address-label">Country:</span>
                  <span className="address-value">{customer.country || '—'}</span>
                </div>
                <div className="address-detail-item">
                  <span className="address-label">Post Code:</span>
                  <span className="address-value">{customer.postCode || '—'}</span>
                </div>
              </div>
            </div>

            <div className='col-12-xs col-6-md'>
              {/* Address Map */}
              <div className="customer-address-map">
                {(() => {
                  const addressParts = [
                    customer.address1,
                    customer.address2,
                    customer.townCity,
                    customer.county,
                    customer.postCode,
                    customer.country
                  ].filter(Boolean);
                  
                  const fullAddress = addressParts.join(', ');
                  
                  return fullAddress ? (
                    <AddressMap 
                      address={fullAddress}
                      customerName={customer.customerName}
                      height="250px"
                    />
                  ) : (
                    <div className="customer-address-placeholder">
                      No address information available
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </section>

        <section className="alarm-content mb-2 p-1">
          <h3 className='heading__title heading__title--info mt-0 mb-1'>Additional Information</h3>
          <div className="details-grid">
            <div><strong>Created At</strong></div><div>{formatDateTime(customer.createdAt)}</div>
            <div><strong>Last Updated</strong></div><div>{formatDateTime(customer.updatedAt)}</div>
            <div><strong>Child Sites</strong></div><div>{sites ? sites.length : '—'}</div>
            <div><strong>Bureau Alarms</strong></div><div>{bureauAlarms.length > 0 ? bureauAlarms.length : '—'}</div>
          </div>
        </section>

        
        {/* Edit Customer Modal */}
        <Modal
          isOpen={openEditModal}
          title="Edit Customer"
          content={
            <div className="modal-form-wrapper">
              <CustomerForm
                onSubmit={handleUpdateCustomer}
                onCancel={() => setOpenEditModal(false)}
                initialData={{
                  customerName: customer.customerName,
                  address1: customer.address1 || '',
                  address2: customer.address2 || '',
                  townCity: customer.townCity || '',
                  county: customer.county || '',
                  country: customer.country || '',
                  postCode: customer.postCode || '',
                }}
                isLoading={submitting}
              />
            </div>
          }
          onConfirm={() => {
            const form = document.querySelector('.modal__body form') as RequestSubmitForm | null;
            if (form) {
              if (typeof form.requestSubmit === 'function') {
                form.requestSubmit();
              } else {
                const evt = new Event('submit', { bubbles: true, cancelable: true });
                form.dispatchEvent(evt);
              }
            }
          }}
          onCancel={() => setOpenEditModal(false)}
        />
         {/* Pending Clients Modal */}
        <Modal
          isOpen={pendingClientsModalOpen}
          title={`Approve Pending Client for ${selectedSiteForAction?.siteName || selectedSiteForAction?.displayName || 'Site'}`}
          content={
            <div className="pending-clients-modal">
              {modalError && (
                <div className="alert alert--error mb-1">
                  {modalError}
                </div>
              )}
              <div className="form-group">
                <h3 className='heading__title--info'>Client Options</h3>
                <div className="">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={approveChange}
                      onChange={(e) => setApproveChange(e.target.checked)}
                      disabled={!!actionInFlight}
                    />
                    <span>Approve Change</span>
                  </label>
                </div>
                <div className="">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={generateToken}
                      onChange={(e) => setGenerateToken(e.target.checked)}
                      disabled={!!actionInFlight}
                    />
                    <span>Generate Token</span>
                  </label>
                </div>
                <div className="form-group">
                  <label htmlFor="approval-notes">Approval Notes (Optional):</label>
                  <textarea
                    id="approval-notes"
                    className="form-input"
                    rows={3}
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    placeholder="Enter any notes about this approval..."
                    disabled={!!actionInFlight}
                  />
                </div>
              </div>
            </div>
          }
          onConfirm={handleApprovePendingClient}
          onCancel={() => {
            setPendingClientsModalOpen(false);
            setPendingClientData(null);
            setSelectedSiteForAction(null);
            setApprovalNotes('');
            setModalError(null);
          }}
        />

        {/* Regenerate Token Modal */}
        <Modal
          isOpen={regenerateTokenModalOpen}
          title={`Regenerate Token for ${selectedSiteForAction?.siteName || selectedSiteForAction?.displayName || 'Site'}`}
          content={
            <div className="regenerate-token-modal">
              {modalError && (
                <div className="alert alert--error mb-1">
                  {modalError}
                </div>
              )}
              <div className="form-group">
                <h3 className='heading__title--info'>Regenerate Token</h3>
                <label htmlFor="token-reason">Reason (Optional):</label>
                <textarea
                  id="token-reason"
                  className="form-input"
                  rows={3}
                  value={tokenReason}
                  onChange={(e) => setTokenReason(e.target.value)}
                  placeholder="Enter reason for regenerating token..."
                  disabled={!!actionInFlight}
                />
              </div>
            </div>
          }
          onConfirm={handleConfirmRegenerateToken}
          onCancel={() => {
            setRegenerateTokenModalOpen(false);
            setSelectedSiteForAction(null);
            setTokenReason('');
            setModalError(null);
          }}
        />

        <Modal
          isOpen={openDeleteSiteModal}
          title={deleteModalHeadingText}
          headerClass="modal__header--danger"
          content={<ModalContent heading={deleteModalHeadingText} message={deleteModalMessageText} />}
          onConfirm={() => {
            deleteResolverRef.current?.(true);
            setOpenDeleteSiteModal(false);
          }}
          onCancel={() => {
            deleteResolverRef.current?.(false);
            setOpenDeleteSiteModal(false);
          }}
        />

        <Modal
          isOpen={assignCategoryModalOpen}
          title={`Assign Category to ${siteForCategoryAssignment?.siteName || siteForCategoryAssignment?.displayName || 'Site'}`}
          content={
            <div className="assign-category-modal">
              {categoryAssignError && <p className="text-danger">{categoryAssignError}</p>}
              <div className="form__group">
                <span className="form__label">Category</span>
                <select
                  id="assign-category-select"
                  className="form__control"
                  value={selectedCategoryId}
                  onChange={e => { setSelectedCategoryId(e.target.value); setCategoryAssignError(null); }}
                  disabled={assigningCategory}
                >
                  <option value="">-- Select a category --</option>
                  {availableCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>
          }
          onConfirm={handleAssignCategoryConfirm}
          onCancel={() => {
            setAssignCategoryModalOpen(false);
            setSiteForCategoryAssignment(null);
            setSelectedCategoryId('');
            setCategoryAssignError(null);
          }}
          okButtonLoading={assigningCategory}
        />
      </div>
    </main>
  );
};

export default CustomerDetails;
